// src/components/common/DeltaChip.tsx
//
// The change-against-the-previous-period chip under a headline figure. The
// arrow follows the change; the colour follows whether that change is good
// for the figure (`goodDirection`): green when it improves, red when it
// worsens, neutral when the change rounds to zero or the figure has no good
// direction (`none`). Defaults to «up is good».
import React from 'react';
import { Box, alpha, useTheme } from '@mui/material';
import { TrendingUp as UpIcon, TrendingDown as DownIcon, TrendingFlat as FlatIcon } from '@mui/icons-material';
import type { FormattedDelta } from '@/lib/analyticsFormat';

export type GoodDirection = 'up' | 'down' | 'none';

/** Whether a change reads as better, worse or neither for a figure. */
export const deltaTone = (trend: FormattedDelta['trend'], goodDirection: GoodDirection = 'up'): 'good' | 'bad' | 'neutral' =>
  trend === 'flat' || goodDirection === 'none' ? 'neutral' : trend === goodDirection ? 'good' : 'bad';

export const DeltaChip: React.FC<{ delta: FormattedDelta; goodDirection?: GoodDirection }> = ({ delta, goodDirection = 'up' }) => {
  const theme = useTheme();
  const quality = deltaTone(delta.trend, goodDirection);
  const tone =
    quality === 'good' ? theme.palette.success.main : quality === 'bad' ? theme.palette.error.main : theme.palette.text.secondary;
  const Icon = delta.trend === 'up' ? UpIcon : delta.trend === 'down' ? DownIcon : FlatIcon;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        px: 1,
        py: 0.25,
        borderRadius: 999,
        fontSize: 12.5,
        fontWeight: 800,
        color: tone,
        backgroundColor: alpha(tone, 0.12),
        '& svg': { fontSize: 14 },
      }}
    >
      <Icon />
      {delta.text}
    </Box>
  );
};
